import {
  BookOpen,
  ChevronDown,
  CircleHelp,
  CreditCard,
  FolderKanban,
  Gauge,
  LogOut,
  Menu,
  Settings,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { useState, type PropsWithChildren } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { canAccessPlatformAdmin } from "../lib/platform-admin";
import { AiPanel } from "../editor/AiPanel";
import { Logo } from "./Logo";

const navItems: Array<{ to: string; label: string; icon: typeof FolderKanban; admin?: boolean; platformAdmin?: boolean }> = [
  { to: "/dashboard", label: "Projetos", icon: FolderKanban },
  // Produção não é mais um destino do menu: ela vive dentro do projeto, ao lado
  // do modelo, porque a pergunta "quanto custa cortar isto" só existe depois de
  // já se estar num projeto.
  { to: "/plan-usage", label: "Uso e plano", icon: Gauge },
  { to: "/settings", label: "Configurações", icon: Settings },
  { to: "/admin", label: "Catálogo e engenharia", icon: Users },
  { to: "/help/pt-BR", label: "Ajuda", icon: CircleHelp },
  { to: "/platform-admin", label: "Admin", icon: ShieldCheck, platformAdmin: true },
];

export function AppShell({ children }: PropsWithChildren) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [workspaceAiOpen, setWorkspaceAiOpen] = useState(false);
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const editorOwnsAgent = location.pathname === "/editor";
  const initials = session?.user.displayName
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Pular para o conteúdo</a>
      <aside className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__brand">
          <Logo inverse />
          <button className="icon-button sidebar__close" onClick={() => setMobileOpen(false)} aria-label="Fechar menu">
            <X size={20} />
          </button>
        </div>
        <nav aria-label="Navegação principal">
          {navItems.filter((item) =>
            (!item.admin || session?.roles.includes("tenant_admin"))
            && (!item.platformAdmin || canAccessPlatformAdmin(session?.platformRoles ?? [])))
            .map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} onClick={() => setMobileOpen(false)}>
              <Icon size={19} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__learn">
          <BookOpen size={18} />
          <div>
            <strong>Primeiros passos</strong>
            <span>5 de 7 concluídos</span>
          </div>
          <div className="mini-progress"><span style={{ width: "71%" }} /></div>
        </div>
        <div className="sidebar__account">
          <NavLink className={({ isActive }) => `sidebar__profile${isActive ? " active" : ""}`} to="/account" onClick={() => setMobileOpen(false)}>
            <span className="avatar">{initials}</span>
            <span>
              <strong>{session?.user.displayName}</strong>
              <small>Configurações do usuário</small>
            </span>
            <Settings size={17} />
          </NavLink>
          <button
            className="sidebar__logout"
            type="button"
            aria-label="Sair da conta"
            title="Sair da conta"
            onClick={async () => {
              await logout();
              navigate("/auth");
            }}
          >
            <LogOut size={17} />
          </button>
        </div>
      </aside>
      {mobileOpen && <button className="sidebar-scrim" onClick={() => setMobileOpen(false)} aria-label="Fechar menu" />}
      <section className="app-shell__body">
        <header className="mobile-header">
          <button className="icon-button" onClick={() => setMobileOpen(true)} aria-label="Abrir menu">
            <Menu size={22} />
          </button>
          <Logo />
          <span className="avatar avatar--small">{initials}</span>
        </header>
        <main id="main-content">{children}</main>
        {!editorOwnsAgent && (
          <>
            {!workspaceAiOpen && (
              <button className="workspace-ai-launch" onClick={() => setWorkspaceAiOpen(true)}>
                <Sparkles size={18} /> meX
              </button>
            )}
            {workspaceAiOpen && (
              <div className="workspace-ai-panel">
                <AiPanel scope="workspace" catalog={[]} onClose={() => setWorkspaceAiOpen(false)} />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
